import { z } from 'zod';

const id = z.string().min(1).max(64);
const change = z.string().trim().min(1).max(500);

export const generationOperationSchema = z.enum([
  'create',
  'copy_edit',
  'layout_edit',
  'image_edit',
  'new_image',
  'video',
]);

export const generationPlanStepSchema = z.strictObject({
  operation: generationOperationSchema,
  targetCardIds: z.array(id).max(10),
  requestedChanges: z.array(change).min(1).max(20),
  preserve: z.array(change).max(20),
});

export const generationPlanSchema = z
  .strictObject({
    schemaVersion: z.literal(1),
    requestId: id,
    originalRequest: z.string().min(1).max(12_000),
    propertyId: id,
    postId: id,
    baseVersionId: id.nullable(),
    operation: generationOperationSchema,
    steps: z.array(generationPlanStepSchema).min(1).max(10).optional(),
    sourceMediaIds: z.array(id).max(24),
    sourceVideoId: id.optional(),
    renderedAssetId: id.nullable(),
    targetCardIds: z.array(id).max(10),
    requestedChanges: z.array(change).min(1).max(20),
    preserve: z.array(change).max(20),
    evidence: z
      .array(z.strictObject({ sourceId: id, fact: z.string().trim().min(1).max(500) }))
      .max(40),
    inspiration: z
      .strictObject({ sourceId: id, form: z.string().trim().min(1).max(2_000) })
      .nullable(),
    status: z.enum(['ready', 'needs_clarification', 'blocked']),
    reasons: z.array(z.string().trim().min(1).max(500)).max(20),
    decisionSource: z.enum(['explicit_ui', 'rule', 'model']),
    maxPaidAttempts: z.number().int().min(0).max(1),
    budgetReference: z.string().trim().min(1).max(200).nullable(),
  })
  .superRefine((plan, ctx) => {
    if (
      plan.operation === 'create' &&
      (plan.baseVersionId !== null ||
        plan.renderedAssetId !== null ||
        (plan.steps && plan.steps.length !== 1) ||
        !plan.targetCardIds.length ||
        plan.targetCardIds.some((id, index) => id !== String(index)))
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['operation'],
        message: 'Creation requires no base and consecutive initial card indices',
      });
    }
    if (plan.steps?.some((step) => step.operation === 'create') && plan.operation !== 'create') {
      ctx.addIssue({
        code: 'custom',
        path: ['steps'],
        message: 'Creation cannot be mixed with revisions',
      });
    }
    if (
      plan.sourceVideoId &&
      (plan.operation !== 'create' ||
        plan.targetCardIds.length !== 1 ||
        plan.targetCardIds[0] !== '0')
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['sourceVideoId'],
        message: 'An original video requires initial creation of one card',
      });
    }
    const imageEdit =
      plan.operation === 'image_edit' || plan.steps?.some((s) => s.operation === 'image_edit');
    if (imageEdit && plan.status === 'ready' && !plan.renderedAssetId) {
      ctx.addIssue({
        code: 'custom',
        path: ['renderedAssetId'],
        message: 'Image edits require the current rendered asset',
      });
    }
    const first = plan.steps?.[0];
    if (
      first &&
      (first.operation !== plan.operation ||
        JSON.stringify(first.targetCardIds) !== JSON.stringify(plan.targetCardIds) ||
        JSON.stringify(first.requestedChanges) !== JSON.stringify(plan.requestedChanges) ||
        JSON.stringify(first.preserve) !== JSON.stringify(plan.preserve))
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['steps', 0],
        message: 'The top-level operation and scope must mirror the first step',
      });
    }
  });

export type GenerationOperation = z.infer<typeof generationOperationSchema>;
export type GenerationPlanStep = z.infer<typeof generationPlanStepSchema>;
export type GenerationPlan = z.infer<typeof generationPlanSchema>;
export type GenerationPlanStatus = GenerationPlan['status'];
export type GenerationDecisionSource = GenerationPlan['decisionSource'];
export type GenerationAcceptance = 'unjudged' | 'accepted' | 'rejected';

export const hostVideoCopySchema = z.strictObject({
  headline: z.string().max(120),
  caption: z.string().max(4000),
  hashtags: z.array(z.string().min(1).max(100)).max(30),
});
export type HostVideoCopy = z.infer<typeof hostVideoCopySchema>;
